import { LocalDate } from "@js-joda/core";
import { DatePickerState, SelectionMode } from "./datePicker.types";
import {
  DatePickerDateFormat,
  formatDateValue,
  formatMultiDateDisplay,
} from "./datePicker.utils";

interface DatePickerDisplayParams {
  readonly allowRange: boolean;
  readonly allowSingleModeMultiSelect: boolean;
  readonly committedMultiKeys: Set<string>;
  readonly committedRangeEnd: LocalDate | null;
  readonly committedRangeStart: LocalDate | null;
  readonly dateFormat: DatePickerDateFormat;
  readonly disabled?: boolean;
  readonly draftValue: string;
  readonly hasError?: boolean;
  readonly hintText?: string;
  readonly isFocused: boolean;
  readonly isOpen: boolean;
  readonly mode: SelectionMode;
  readonly multiSelectedKeys: Set<string>;
  readonly rangeEnd: LocalDate | null;
  readonly rangeStart: LocalDate | null;
  readonly resolvedErrorText?: string;
  readonly value?: LocalDate | null;
}

export interface DatePickerDisplayState {
  readonly isFilled: boolean;
  readonly messageText?: string;
  readonly showValue: string;
  readonly visualState: DatePickerState;
}

export function getDatePickerDisplayState(
  params: DatePickerDisplayParams,
): DatePickerDisplayState {
  const showValue = params.isOpen
    ? getDraftDisplay(params)
    : getCommittedDisplay(params);
  const isFilled = getIsFilled(params, showValue);
  const visualState = getVisualState(params, isFilled);
  return {
    isFilled,
    messageText:
      visualState === "error" ? params.resolvedErrorText : params.hintText,
    showValue,
    visualState,
  };
}

function getDraftDisplay(params: DatePickerDisplayParams): string {
  if (!params.allowRange) {
    return formatDateValue(params.value ?? null, params.dateFormat);
  }
  if (params.mode === "range") return getDraftRangeDisplay(params);
  if (!params.allowSingleModeMultiSelect) {
    return formatDateValue(params.value ?? null, params.dateFormat);
  }
  return formatMultiDateDisplay(params.multiSelectedKeys, params.dateFormat);
}

function getDraftRangeDisplay(params: DatePickerDisplayParams): string {
  if (params.rangeStart && params.rangeEnd) {
    return `${formatDateValue(params.rangeStart, params.dateFormat)} – ${formatDateValue(
      params.rangeEnd,
      params.dateFormat,
    )}`;
  }
  if (params.rangeStart) {
    return `${formatDateValue(params.rangeStart, params.dateFormat)} – …`;
  }
  return "";
}

function getCommittedDisplay(params: DatePickerDisplayParams): string {
  if (!params.allowRange) {
    return formatDateValue(params.value ?? null, params.dateFormat);
  }
  if (params.mode === "range") return getCommittedRangeDisplay(params);
  if (!params.allowSingleModeMultiSelect) {
    return formatDateValue(params.value ?? null, params.dateFormat);
  }
  return formatMultiDateDisplay(params.committedMultiKeys, params.dateFormat);
}

function getCommittedRangeDisplay(params: DatePickerDisplayParams): string {
  if (!params.committedRangeStart || !params.committedRangeEnd) return "";
  return `${formatDateValue(
    params.committedRangeStart,
    params.dateFormat,
  )} – ${formatDateValue(params.committedRangeEnd, params.dateFormat)}`;
}

function getIsFilled(
  params: DatePickerDisplayParams,
  showValue: string,
): boolean {
  if (params.allowRange) return showValue.length > 0;
  const inputValue = params.isFocused ? params.draftValue : showValue;
  return inputValue.trim().length > 0;
}

function getVisualState(
  params: DatePickerDisplayParams,
  isFilled: boolean,
): DatePickerState {
  if (params.disabled) return "disabled";
  if (params.hasError) return "error";
  if (params.isOpen) return "active";
  if (isFilled) return "filled";
  return "inactive";
}
